import { useMemo, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { TherapistAvailability } from 'domains/calendars/v3/useEvaluationCalendarSummary';
import { TimeOptionValue } from 'components/TimeOptionsCheckboxes/TimeOptionsCheckboxes';
import { DEFAULT_TIME_OPTIONS } from 'domains/signupDirect/misc/constants';

type UseTimeOptionsFilterParams = {
  availabilityByTherapist: TherapistAvailability[];
  displayTimezone?: { value: string };
  enabled: boolean;
};

export const useTimeOptionsFilter = ({
  availabilityByTherapist,
  displayTimezone,
  enabled,
}: UseTimeOptionsFilterParams) => {
  const [selectedTimeOptions, setSelectedTimeOptions] = useState<TimeOptionValue>(DEFAULT_TIME_OPTIONS);

  const isTimeSlotInSelectedRange = (slotTime: Date): boolean => {
    if (Object.values(selectedTimeOptions).every(value => !value)) {
      return true;
    }

    if (!displayTimezone) {
      return true;
    }

    const hoursInDisplayTimezone = parseInt(formatInTimeZone(slotTime, displayTimezone.value, 'H'), 10);

    if (selectedTimeOptions.earlyMorning && hoursInDisplayTimezone < 10) {
      return true;
    }

    if (selectedTimeOptions.morning && hoursInDisplayTimezone >= 10 && hoursInDisplayTimezone < 12) {
      return true;
    }

    if (selectedTimeOptions.afternoon && hoursInDisplayTimezone >= 12 && hoursInDisplayTimezone < 17) {
      return true;
    }

    if (selectedTimeOptions.evening && hoursInDisplayTimezone >= 17) {
      return true;
    }

    return false;
  };

  const filteredAvailabilityByTherapist = useMemo(() => {
    if (!enabled) {
      return availabilityByTherapist;
    }

    return availabilityByTherapist
      .map(therapistAvailability => ({
        ...therapistAvailability,
        timeSlots: therapistAvailability.timeSlots.filter(slot => isTimeSlotInSelectedRange(slot.time)),
      }))
      .filter(therapistAvailability => therapistAvailability.timeSlots.length > 0);
  }, [availabilityByTherapist, selectedTimeOptions, displayTimezone, enabled]);

  const handleTimeOptionsChange = (timeOptions: TimeOptionValue) => {
    setSelectedTimeOptions(timeOptions);
  };

  const noSlotsMatchTimeFilters =
    enabled && filteredAvailabilityByTherapist.length === 0 && availabilityByTherapist.length > 0;

  return {
    selectedTimeOptions,
    filteredAvailabilityByTherapist,
    handleTimeOptionsChange,
    noSlotsMatchTimeFilters,
  };
};
